"use client";

import Autoplay from "embla-carousel-autoplay";
import { useCallback, useEffect, useRef, useState } from "react";
import { CarouselApi } from "~/components/ui/carousel";

const AUTOPLAY_DELAY = 8000;
const DRAG_THRESHOLD = 4;

const carouselOpts = {
    duration: 0,
} as const;

type UseTimedCarouselOptions = {
    onManualSelect?: (index: number) => void;
};

export const useTimedCarousel = ({
    onManualSelect,
}: UseTimedCarouselOptions = {}) => {
    const [api, setApi] = useState<CarouselApi>();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [enterIndex, setEnterIndex] = useState<number | null>(null);
    const [isCarouselVisible, setIsCarouselVisible] = useState(false);
    const [isDocumentVisible, setIsDocumentVisible] = useState(true);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(
        () => typeof window !== "undefined"
            && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
    const [plugins] = useState(() => [
        Autoplay({
            delay: AUTOPLAY_DELAY,
            jump: true,
            playOnInit: false,
            stopOnInteraction: true,
            stopOnMouseEnter: false,
            stopOnFocusIn: false,
        }),
    ]);

    const carouselRef = useRef<HTMLElement>(null);
    const progressFillRef = useRef<HTMLSpanElement>(null);
    const isDraggingRef = useRef(false);
    const isManualNavigationRef = useRef(false);
    const lastIndexRef = useRef(0);
    const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
    const hasPointerMovedRef = useRef(false);
    const shouldAutoplay = isCarouselVisible
        && isDocumentVisible
        && !prefersReducedMotion;

    const resetAutoplay = useCallback(() => {
        if (!shouldAutoplay) return;

        api?.plugins()?.autoplay?.reset();
    }, [api, shouldAutoplay]);

    const scrollPrev = useCallback(() => {
        if (!api) return;

        isManualNavigationRef.current = true;
        api.scrollPrev();
    }, [api]);

    const scrollNext = useCallback(() => {
        if (!api) return;

        isManualNavigationRef.current = true;
        api.scrollNext();
    }, [api]);

    const scrollTo = useCallback((index: number) => {
        if (index === lastIndexRef.current) {
            resetAutoplay();
            return;
        }

        if (!api) return;

        isManualNavigationRef.current = true;
        api.scrollTo(index);
    }, [api, resetAutoplay]);

    const finishPointerInteraction = useCallback(() => {
        if (!isDraggingRef.current) return;

        isDraggingRef.current = false;
        carouselRef.current?.removeAttribute("data-dragging");

        if (shouldAutoplay) {
            api?.plugins()?.autoplay?.play();
        }
    }, [api, shouldAutoplay]);

    useEffect(() => {
        const carousel = carouselRef.current;
        if (!carousel) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsCarouselVisible(Boolean(entry?.isIntersecting));
            },
            { rootMargin: "0px", threshold: 0.15 }
        );

        observer.observe(carousel);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const onVisibilityChange = () => {
            setIsDocumentVisible(document.visibilityState === "visible");
        };

        onVisibilityChange();
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        const onChange = (event: MediaQueryListEvent) => {
            setPrefersReducedMotion(event.matches);
        };

        mediaQuery.addEventListener("change", onChange);

        return () => {
            mediaQuery.removeEventListener("change", onChange);
        };
    }, []);

    useEffect(() => {
        const autoplay = api?.plugins()?.autoplay;
        if (!autoplay) return;

        if (shouldAutoplay) {
            autoplay.play();
        } else {
            autoplay.stop();
        }
    }, [api, shouldAutoplay]);

    useEffect(() => {
        if (!api) return;

        const onSelect = () => {
            const index = api.selectedScrollSnap();

            if (index === lastIndexRef.current) return;

            lastIndexRef.current = index;
            setSelectedIndex(index);
            const isManualSelection = isDraggingRef.current
                || isManualNavigationRef.current;
            isManualNavigationRef.current = false;

            if (isManualSelection) {
                onManualSelect?.(index);
            }

            if (isDraggingRef.current) {
                setEnterIndex(null);
                return;
            }

            setEnterIndex(index);
            resetAutoplay();
        };

        const onPointerDown = () => {
            isDraggingRef.current = true;
            carouselRef.current?.setAttribute("data-dragging", "true");
        };

        const onSettle = () => {
            finishPointerInteraction();
        };

        onSelect();
        api.on("select", onSelect);
        api.on("reInit", onSelect);
        api.on("pointerDown", onPointerDown);
        api.on("settle", onSettle);

        return () => {
            api.off("select", onSelect);
            api.off("reInit", onSelect);
            api.off("pointerDown", onPointerDown);
            api.off("settle", onSettle);
        };
    }, [api, finishPointerInteraction, onManualSelect, resetAutoplay]);

    useEffect(() => {
        const carousel = carouselRef.current;

        if (!carousel) return;

        const onPointerDown = (event: PointerEvent) => {
            pointerStartRef.current = {
                x: event.clientX,
                y: event.clientY,
            };
            hasPointerMovedRef.current = false;
        };

        const onPointerMove = (event: PointerEvent) => {
            const start = pointerStartRef.current;

            if (!start) return;

            const distance = Math.hypot(
                event.clientX - start.x,
                event.clientY - start.y
            );

            if (distance >= DRAG_THRESHOLD) {
                hasPointerMovedRef.current = true;
            }
        };

        const onPointerUp = () => {
            if (!hasPointerMovedRef.current) {
                finishPointerInteraction();
            }

            pointerStartRef.current = null;
        };

        carousel.addEventListener("pointerdown", onPointerDown, true);
        carousel.addEventListener("pointermove", onPointerMove, true);
        carousel.addEventListener("pointerup", onPointerUp, true);

        return () => {
            carousel.removeEventListener("pointerdown", onPointerDown, true);
            carousel.removeEventListener("pointermove", onPointerMove, true);
            carousel.removeEventListener("pointerup", onPointerUp, true);
        };
    }, [finishPointerInteraction]);

    useEffect(() => {
        if (!api || !shouldAutoplay) return;

        let frameId = 0;

        const updateProgress = () => {
            const fill = progressFillRef.current;
            const remaining = api.plugins()?.autoplay?.timeUntilNext();

            if (fill) {
                const progress = remaining == null
                    ? 0
                    : 1 - remaining / AUTOPLAY_DELAY;

                fill.style.transform = `scaleX(${Math.min(1, Math.max(0, progress))})`;
            }

            frameId = window.requestAnimationFrame(updateProgress);
        };

        frameId = window.requestAnimationFrame(updateProgress);

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [api, shouldAutoplay]);

    return {
        setApi,
        opts: carouselOpts,
        plugins,
        selectedIndex,
        enterIndex,
        carouselRef,
        progressFillRef,
        scrollPrev,
        scrollNext,
        scrollTo,
    };
};
